import {
  type DocumentData,
  type QueryDocumentSnapshot,
  type WithFieldValue,
  type FirestoreDataConverter,
  serverTimestamp,
  Timestamp as FirestoreTimestamp,
} from 'firebase/firestore';
import { getAppConfig } from '@/config/appConfig.js';

export const getCurrentEnvironment = (): string => getAppConfig().dataEnvironment;

export const toDate = (value: unknown): Date | undefined => {
  if (value instanceof FirestoreTimestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value);
  return undefined;
};

export const toTimestampString = (value: unknown): string | undefined => {
  const date = toDate(value);
  return date?.toISOString();
};

export const buildConverter = <T extends Record<string, unknown>>(
  from: (data: DocumentData) => T,
  to: (model: T) => WithFieldValue<DocumentData>
): FirestoreDataConverter<T> => ({
  fromFirestore: (snapshot: QueryDocumentSnapshot<DocumentData>): T => from(snapshot.data()),
  toFirestore: (model: T): WithFieldValue<DocumentData> => to(model),
});

export const withEnvironment = <T extends Record<string, unknown>>(model: T): T => ({
  ...model,
  environment: getCurrentEnvironment(),
});

export const withTimestamp = <T extends Record<string, unknown>>(model: T, isNew = true): T => {
  const now = serverTimestamp();
  if (isNew) {
    return { ...model, createdAt: now, updatedAt: now };
  }
  return { ...model, updatedAt: now };
};
